import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ScanLine, School } from 'lucide-react';
import { ApiService } from '../../services/api';
import LgsOpticalUploadModal from '../practice-exams/components/LgsOpticalUploadModal';
import type { PracticeExam } from '../../types/practice-exam';
import type { PartnerOverview } from '../../types/external-school';

/**
 * Kırtasiye: kendisine bağlı okulların optik dosyalarını kurum dışına açık LGS denemelerine yükler. Öğrenci listesi ve
 * sonuçlar görünmez; yalnız yükleme özeti.
 */
export default function KirtasiyeUpload() {
    const [overview, setOverview] = useState<PartnerOverview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [exam, setExam] = useState<PracticeExam | null>(null);

    const load = useCallback(() => {
        setError(null);
        ApiService.getExternalPartnerOverview()
            .then(setOverview)
            .catch((err) => setError(err?.message || 'Bilgiler alınamadı'));
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-6">
            <div>
                <h1 className="text-xl font-semibold text-gray-900">Deneme optik yükleme</h1>
                <p className="mt-1 text-sm text-gray-600">
                    Okullarınızın optik okuyucu dosyalarını (form tanımı + sonuç) ilgili denemeye yükleyin. Öğrenciler karnelerine
                    okullarının bağlantısından T.C. kimlik ve okul numarasıyla ulaşır.
                </p>
            </div>

            {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {!overview && !error && <div className="py-10 text-center text-gray-500"><Loader2 className="mx-auto animate-spin" /></div>}

            {overview && (
                <>
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <h2 className="mb-2 text-sm font-semibold text-gray-700">Okullarınız</h2>
                        {overview.schools.length === 0 ? (
                            <p className="text-sm text-gray-500">Size bağlı okul yok. Okul eklenmesi için bizimle iletişime geçin.</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {overview.schools.map((school) => (
                                    <span key={school.id} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
                                        <School size={14} /> {school.name}{school.opticalCodes?.length ? ` · ${school.opticalCodes.join(', ')}` : ''}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <h2 className="text-sm font-semibold text-gray-700">Denemeler</h2>
                        {overview.exams.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-gray-300 py-8 text-center text-sm text-gray-500">Yükleme açık deneme yok.</div>
                        ) : overview.exams.map((row) => (
                            <div key={row.id} className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <div className="font-medium text-gray-900">{row.name}</div>
                                    <div className="text-xs text-gray-500">
                                        {row.date ? new Date(row.date).toLocaleDateString('tr-TR') : ''}{row.gradeLevel ? ` · ${row.gradeLevel}. sınıf` : ''}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setExam({ id: row.id, name: row.name, date: row.date, levelType: 'lgs', systemType: 'internal', audience: 'external', gradeLevel: row.gradeLevel } as PracticeExam)}
                                    disabled={overview.schools.length === 0}
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                                    <ScanLine size={16} /> Optik yükle
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <LgsOpticalUploadModal
                exam={exam}
                isOpen={Boolean(exam)}
                onClose={() => setExam(null)}
                partnerMode
                schoolOptions={overview?.schools || []}
            />
        </div>
    );
}
